import React, { useState, useEffect, useCallback } from 'react';
import { Menu, Search, Bell, Plus, X, Calendar, Users, FileText, Sparkles, AlertTriangle, CheckCircle2, Info, Trash2, Mail, CreditCard } from 'lucide-react';
import ConnectionStatus from './ConnectionStatus';
import { OfflineBadge } from './OfflineIndicator';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Header = ({ onMenuClick, pageTitle = '', onNavigate, onSearch, user }) => {
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [canvassAlert, setCanvassAlert] = useState(null);
  
  // Check if user is a worker
  const isWorker = user?.role === 'Worker';

  // Fetch notifications from backend
  const fetchNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('streakfree_token');
      if (!token) return;
      
      const res = await fetch(`${API_URL}/api/notifications?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
        // Full-screen alert for a new canvassing sheet (once per notification)
        const seen = JSON.parse(sessionStorage.getItem('canvassAlertsSeen') || '[]');
        const fresh = (data.notifications || []).find(n => n.type === 'canvassing' && !n.read && !seen.includes(n.id));
        if (fresh) {
          sessionStorage.setItem('canvassAlertsSeen', JSON.stringify([...seen, fresh.id].slice(-20)));
          setCanvassAlert(fresh);
        }
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  }, []);

  // Load notifications on mount and poll every 30 seconds (silent — only the
  // bell badge count updates, no full UI refresh).
  useEffect(() => {
    fetchNotifications();
    
    const interval = setInterval(fetchNotifications, 30000);
    
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Auto-open the bell dropdown when arriving from a push-notification tap.
  // Push payloads target `/dashboard?openNotifications=1` so the user lands
  // directly on their unread alerts.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('openNotifications') === '1') {
      setShowNotifications(true);
      fetchNotifications();
      // Clean the flag from the URL so a refresh doesn't reopen it.
      params.delete('openNotifications');
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    }

    // Service worker can also message us when a push is tapped while the
    // app is already open in another tab/window.
    const onSwMessage = (event) => {
      if (event?.data?.type === 'NOTIFICATION_CLICK') {
        setShowNotifications(true);
        fetchNotifications();
      }
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onSwMessage);
    }
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', onSwMessage);
      }
    };
  }, [fetchNotifications]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (err) {
      console.error('Error marking notification read:', err);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('streakfree_token');
      await fetch(`${API_URL}/api/notifications/mark-all-read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (err) {
      console.error('Error marking all notifications read:', err);
    }
  };

  // Delete notification
  const deleteNotification = async (e, notificationId) => {
    e.stopPropagation(); // Prevent triggering the click handler
    try {
      const token = localStorage.getItem('streakfree_token');
      await fetch(`${API_URL}/api/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  // Handle notification click - navigate to customer
  const handleNotificationClick = (notif) => {
    if (notif.type === 'canvassing') {
      if (onNavigate) onNavigate('canvassing');
      markAsRead(notif.id);
      setShowNotifications(false);
      return;
    }
    // Get customer_id from either top-level or nested in data object
    const customerId = notif.customer_id || notif.data?.customer_id;
    if (customerId) {
      // Store customer ID to open after navigation
      sessionStorage.setItem('openCustomerId', customerId);
      if (onNavigate) {
        onNavigate('customers');
      }
      // Force a small delay then dispatch custom event to trigger re-check
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('openCustomerFromNotification', { detail: customerId }));
      }, 100);
    }
    markAsRead(notif.id);
    setShowNotifications(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Store search query for customers page to pick up
      sessionStorage.setItem('customerSearchQuery', searchQuery.trim());
      if (onNavigate) {
        onNavigate('customers');
      }
    }
    setShowSearch(false);
    setSearchQuery('');
  };

  const handleNewAction = (action) => {
    setShowNewMenu(false);
    if (onNavigate) {
      // Store flag to open create form
      sessionStorage.setItem('openCreateForm', 'true');
      onNavigate(action);
    }
    // Also dispatch a custom event so already-mounted pages react immediately
    // (navigateToPage is a no-op when target page === current page, so the
    // sessionStorage flag would otherwise sit unread until next mount).
    try {
      window.dispatchEvent(new CustomEvent('streakfree:open-create-form', { detail: { action } }));
    } catch (e) {
      // Older browsers — ignore, sessionStorage flag still works on mount
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'email_customer':
        return <Mail size={16} className="text-blue-500" />;
      case 'pdf_import':
        return <FileText size={16} className="text-purple-500" />;
      case 'card_added':
        return <CreditCard size={16} className="text-emerald-500" />;
      case 'error':
        return <AlertTriangle size={16} className="text-red-500" />;
      case 'warning':
        return <AlertTriangle size={16} className="text-amber-500" />;
      case 'success':
        return <CheckCircle2 size={16} className="text-emerald-500" />;
      default:
        return <Info size={16} className="text-blue-500" />;
    }
  };

  const getNotificationBg = (type, read) => {
    if (read) return 'bg-white';
    switch (type) {
      case 'email_customer':
        return 'bg-blue-50';
      case 'pdf_import':
        return 'bg-purple-50';
      case 'card_added':
        return 'bg-emerald-50';
      case 'error':
        return 'bg-red-50';
      case 'warning':
        return 'bg-amber-50';
      case 'success':
        return 'bg-emerald-50';
      default:
        return 'bg-blue-50';
    }
  };

  return (
    <header className="text-white sticky top-0 z-20" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 2px)', backgroundColor: 'var(--theme-primary)' }}>
      {/* Main header bar - more compact on mobile */}
      <div className="px-2 sm:px-4 py-0.5 sm:py-3 flex items-center justify-between min-h-[32px] sm:min-h-[48px]">
        <div className="flex items-center gap-2 sm:gap-3">
          <button 
            onClick={onMenuClick}
            className="p-1.5 sm:p-3 hover:bg-white/10 rounded-xl transition-colors lg:hidden min-w-[32px] min-h-[32px] sm:min-w-[44px] sm:min-h-[44px] flex items-center justify-center"
            data-testid="menu-toggle"
          >
            <Menu size={20} strokeWidth={1.5} />
          </button>
          {pageTitle && (
            <div>
              <h1 className="text-lg sm:text-xl font-bold tracking-tight">{pageTitle}</h1>
            </div>
          )}
        </div>
        
        {/* Action icons - inline connection status */}
        <div className="flex items-center gap-0.5 sm:gap-1 relative">
          {/* Connection Status - inline and compact */}
          <div className="flex items-center mr-1">
            <ConnectionStatus />
          </div>
          
          {/* Search */}
          <button 
            onClick={() => setShowSearch(!showSearch)}
            className="p-1.5 sm:p-3 hover:bg-white/10 rounded-xl transition-colors min-w-[32px] min-h-[32px] sm:min-w-[44px] sm:min-h-[44px] flex items-center justify-center"
            title="Search"
            data-testid="header-search-btn"
          >
            <Search size={18} strokeWidth={1.5} />
          </button>
          
          {/* Notifications */}
          <button 
            onClick={() => setShowNotifications(!showNotifications)}
            className="p-1.5 sm:p-3 hover:bg-white/10 rounded-xl transition-colors relative min-w-[32px] min-h-[32px] sm:min-w-[44px] sm:min-h-[44px] flex items-center justify-center"
            title="Notifications"
            data-testid="header-notifications-btn"
          >
            <Bell size={18} strokeWidth={1.5} />
            {unreadCount > 0 && (
              <span className="absolute top-0.5 right-0.5 min-w-[16px] h-[16px] flex items-center justify-center bg-red-500 text-white text-[9px] font-bold rounded-full px-0.5">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          
          {/* Add New - Hidden for workers */}
          {!isWorker && (
            <button 
              onClick={() => setShowNewMenu(!showNewMenu)}
              className="ml-1 sm:ml-2 flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-3 rounded-xl transition-colors font-medium text-sm min-h-[32px] sm:min-h-[44px] text-white"
              style={{ backgroundColor: 'var(--theme-button)' }}
              title="Add New"
              data-testid="header-new-btn"
            >
              <Plus size={16} strokeWidth={2} />
              <span className="hidden sm:inline">New</span>
            </button>
          )}
        </div>
      </div>

      {/* Search Dropdown */}
      {showSearch && (
        <div className="absolute top-full left-0 right-0 border-t border-white/10 p-4 shadow-xl" style={{ backgroundColor: 'var(--theme-primary-light)' }}>
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="relative">
              <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search customers, jobs, invoices..."
                className="w-full pl-12 pr-12 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-zinc-400 focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': 'var(--theme-accent)' }}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowSearch(false)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Notifications Dropdown */}
      {showNotifications && (
        <div className="absolute top-full right-4 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-zinc-200 mt-2 overflow-hidden z-50 max-h-[70vh] flex flex-col">
          <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
            <h3 className="font-semibold text-zinc-900">Notifications</h3>
            {notifications.length > 0 && (
              <button 
                onClick={() => markAllAsRead()}
                className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-zinc-500">
                <Bell size={32} className="mx-auto mb-2 text-zinc-300" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {notifications.map(notif => (
                  <div 
                    key={notif.id}
                    onClick={() => handleNotificationClick(notif)}
                    className={`p-3 hover:bg-zinc-50 transition-colors cursor-pointer ${getNotificationBg(notif.type, notif.read)} ${!notif.read ? 'border-l-4 border-l-blue-500' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">
                        {getNotificationIcon(notif.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-zinc-900">{notif.title}</p>
                        <p className="text-xs text-zinc-600 mt-0.5 line-clamp-2">{notif.message}</p>
                        {notif.customer_name && (
                          <p className="text-xs text-blue-600 mt-1 font-medium">Click to view {notif.customer_name}</p>
                        )}
                        <p className="text-xs text-zinc-400 mt-1">{formatTimestamp(notif.created_at)}</p>
                      </div>
                      <button
                        onClick={(e) => deleteNotification(e, notif.id)}
                        className="p-1.5 hover:bg-red-100 rounded-lg transition-colors group"
                        title="Delete notification"
                      >
                        <Trash2 size={14} className="text-zinc-400 group-hover:text-red-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* New Menu Dropdown */}
      {showNewMenu && (
        <div className="absolute top-full right-4 w-56 bg-white rounded-xl shadow-2xl border border-zinc-200 mt-2 overflow-hidden z-50">
          <div className="py-2">
            <button
              onClick={() => handleNewAction('jobs')}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors text-left"
            >
              <Calendar size={20} className="text-purple-500" />
              <span className="text-zinc-700 font-medium">New Job</span>
            </button>
            <button
              onClick={() => handleNewAction('customers')}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors text-left"
            >
              <Users size={20} className="text-blue-500" />
              <span className="text-zinc-700 font-medium">New Customer</span>
            </button>
            <button
              onClick={() => handleNewAction('invoices')}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors text-left"
            >
              <FileText size={20} className="text-amber-500" />
              <span className="text-zinc-700 font-medium">New Invoice</span>
            </button>
            <button
              onClick={() => handleNewAction('leads')}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors text-left"
            >
              <Sparkles size={20} className="text-emerald-500" />
              <span className="text-zinc-700 font-medium">New Lead</span>
            </button>
          </div>
        </div>
      )}

      {/* Click outside to close dropdowns */}
      {(showSearch || showNotifications || showNewMenu) && (
        <div 
          className="fixed inset-0 z-30" 
          onClick={() => {
            setShowSearch(false);
            setShowNotifications(false);
            setShowNewMenu(false);
          }}
          style={{ background: 'transparent' }}
        />
      )}

      {/* Full-screen alert: new canvassing sheet arrived */}
      {canvassAlert && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="canvass-alert-overlay">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-5 flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                <FileText size={24} />
              </div>
              <div>
                <h2 className="text-lg font-bold">New canvassing sheet</h2>
                <p className="text-sm text-emerald-100">Waiting for your review</p>
              </div>
            </div>
            <div className="p-6">
              <p className="text-sm text-zinc-700" data-testid="canvass-alert-message">{canvassAlert.message}</p>
              <div className="flex gap-3 mt-5">
                <button
                  onClick={() => {
                    markAsRead(canvassAlert.id);
                    setCanvassAlert(null);
                    if (onNavigate) onNavigate('canvassing');
                  }}
                  className="flex-1 py-3 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600"
                  data-testid="canvass-alert-review-btn"
                >
                  Review now
                </button>
                <button
                  onClick={() => setCanvassAlert(null)}
                  className="px-5 py-3 bg-zinc-100 text-zinc-600 rounded-xl font-semibold hover:bg-zinc-200"
                  data-testid="canvass-alert-later-btn"
                >
                  Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
